import type { TierId } from "../api/types";
import { Plans } from "./Plans";

interface Props {
  current: TierId;
  onChoose: (tier: TierId) => void;
  busyTier: TierId | null;
}

/**
 * The pricing table with the copy that fits who is reading it.
 *
 * A subscriber sees this too, not just a Preview visitor: it used to render
 * only for the free tier, which left a County subscriber with no way to reach
 * State at all. Someone already paying is being told what switching costs, not
 * sold to from scratch — the number that matters to them is the difference.
 *
 * Every tier gets an entry, the top one included. A plan a customer can enter
 * and not leave is a trap, and leaving it is not the billing portal's job:
 * cancelling is not the same as moving down, and making someone cancel to do
 * it costs them their coverage in between.
 */
const BLURBS: Partial<Record<TierId, { heading: string; body: string }>> = {
  free: {
    heading: "You're seeing the preview",
    body:
      "The ten worst buildings in whichever county you're looking at, named " +
      "and scored in full — but without parcel folios, filters or parcel " +
      "records. County covers one county for $29 — its parcels, its " +
      "milestone standing, and whatever enforcement records it publishes. " +
      "State covers every county we hold, and every one we add, for $99.",
  },
  county: {
    heading: "You're on County",
    body:
      "One county — its parcels, its milestone standing, and whatever " +
      "enforcement records it publishes. State covers every county we hold " +
      "and every one we add, for $99. Switching is prorated — the time you have already " +
      "paid for comes off, so you pay the difference for the rest of this " +
      "month and your billing date doesn't move.",
  },
  state: {
    heading: "You're on State",
    body:
      "Every county we hold, and every one we add. If one county is all you " +
      "need, County is $29 and you can move which one it covers monthly. " +
      "Nothing is charged to move down — the time you've already paid for " +
      "becomes a credit against your next invoice.",
  },
};

export function Upsell({ current, onChoose, busyTier }: Props) {
  const blurb = BLURBS[current];
  if (!blurb) return null;

  return (
    <div className="upsell">
      <div className="blurb">
        <h4>{blurb.heading}</h4>
        <p>{blurb.body}</p>
      </div>
      <div className="picker">
        <Plans
          current={current}
          onChoose={onChoose}
          busyTier={busyTier}
          subscribed={current !== "free"}
        />
      </div>
    </div>
  );
}
